import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { emojis, emojiCategories, type EmojiItem } from './emojiData';

const RECENT_KEY = 'elefa_recent_emojis';
const MAX_RECENT = 32;

function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function addRecent(emoji: string) {
  const list = getRecent().filter(e => e !== emoji);
  list.unshift(emoji);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
}

// Memoji-style stickers (emoji-based expressive stickers rendered large)
const stickers = [
  '😀', '😍', '🤣', '😭', '🥺', '😎', '🤔', '👋🏾',
  '🙌🏿', '🔥', '💀', '🥳', '🤯', '😤', '🫶🏾', '🤗',
  '😈', '🤡', '👻', '💩', '🤮', '🥵', '🥶', '😴',
];

interface EmojiKeyboardProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEmoji: (emoji: string) => void;
  onSendSticker?: (sticker: string) => void;
}

export function EmojiKeyboard({ isOpen, onClose, onSelectEmoji, onSendSticker }: EmojiKeyboardProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('smileys');
  const [activeTab, setActiveTab] = useState<'emoji' | 'stickers'>('emoji');
  const [recentEmojis, setRecentEmojis] = useState<string[]>(getRecent);

  useEffect(() => {
    if (isOpen) setRecentEmojis(getRecent());
  }, [isOpen]);

  const filteredEmojis = useMemo(() => {
    if (search.trim()) {
      return emojis.filter(e => e.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (activeCategory === 'recent') {
      return recentEmojis.map(emoji => {
        const found = emojis.find(e => e.emoji === emoji);
        return found || { emoji, name: '', category: 'recent' };
      });
    }
    return emojis.filter(e => e.category === activeCategory);
  }, [search, activeCategory, recentEmojis]);

  const handleSelect = useCallback((emoji: string) => {
    addRecent(emoji);
    setRecentEmojis(getRecent());
    onSelectEmoji(emoji);
  }, [onSelectEmoji]);

  const handleStickerTap = useCallback((sticker: string) => {
    onSendSticker?.(sticker);
  }, [onSendSticker]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className="border-t border-border/40 bg-card"
        >
          {/* Tab selector: Emoji | Stickers */}
          <div className="flex border-b border-border/30">
            <button
              onClick={() => setActiveTab('emoji')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                activeTab === 'emoji' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
              }`}
            >
              Emojis
            </button>
            <button
              onClick={() => setActiveTab('stickers')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                activeTab === 'stickers' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
              }`}
            >
              Stickers
            </button>
          </div>

          {activeTab === 'emoji' ? (
            <>
              {/* Search */}
              <div className="px-3 py-2 flex items-center gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search emoji"
                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-muted/50 border-0 outline-none focus:ring-1 focus:ring-primary/20 placeholder:text-muted-foreground/40"
                  />
                </div>
                <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Emoji grid */}
              <ScrollArea className="h-[220px]">
                <div className="px-2 pb-1">
                  {!search && activeCategory !== 'recent' && (
                    <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider px-1 py-1">
                      {emojiCategories.find(c => c.id === activeCategory)?.name}
                    </p>
                  )}
                  {!search && activeCategory === 'recent' && recentEmojis.length === 0 && (
                    <p className="text-xs text-muted-foreground/50 text-center py-8">No recent emojis</p>
                  )}
                  <div className="grid grid-cols-8 gap-0.5">
                    {filteredEmojis.map((item, i) => (
                      <button
                        key={`${item.emoji}-${i}`}
                        onClick={() => handleSelect(item.emoji)}
                        className="w-full aspect-square flex items-center justify-center text-[22px] rounded-lg hover:bg-muted/60 active:scale-90 transition-transform"
                        title={item.name}
                      >
                        {item.emoji}
                      </button>
                    ))}
                  </div>
                  {search && filteredEmojis.length === 0 && (
                    <p className="text-xs text-muted-foreground/50 text-center py-8">No emojis found</p>
                  )}
                </div>
              </ScrollArea>

              {/* Category tabs */}
              <div className="flex items-center border-t border-border/30 px-1 py-1 gap-0.5 overflow-x-auto">
                {emojiCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCategory(cat.id); setSearch(''); }}
                    className={`flex-shrink-0 w-9 h-8 flex items-center justify-center text-base rounded-md transition-colors ${
                      activeCategory === cat.id && !search ? 'bg-muted' : 'hover:bg-muted/40'
                    }`}
                    title={cat.name}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* Stickers tab */
            <ScrollArea className="h-[268px]">
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider px-1 py-1 mb-1">
                  Memoji Stickers
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {stickers.map((sticker) => (
                    <motion.button
                      key={sticker}
                      whileTap={{ scale: 0.85 }}
                      onClick={() => handleStickerTap(sticker)}
                      className="aspect-square flex items-center justify-center text-[48px] rounded-2xl bg-muted/30 hover:bg-muted/60 transition-colors"
                    >
                      {sticker}
                    </motion.button>
                  ))}
                </div>
              </div>
            </ScrollArea>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
